import { useCallback, useMemo } from 'react';
import { useGlobal } from 'reactn';
import { CASE_PLAN_SECTION_WRITE_PERMISSIONS } from '../../constants/reentry-planning.constants';
import { isAnyPermitted } from '../../utils/case-plan-permission.util';
import {
  canManageCasePlanSections,
  excludePendingSections,
  resolveSectionDestructiveAction,
  resolveSectionMoveBoundaries,
} from '../../utils/case-plan-section.util';
import { useCasePlanSectionConfirmation } from './useCasePlanSectionConfirmation';
import { useCasePlanSectionMutations } from './useCasePlanSectionMutations';
import { useCasePlanSectionTabs } from './useCasePlanSectionTabs';

/**
 * Omitting a callback is the shared section component's read-only contract, so a section the
 * staff member may not edit receives no handler at all rather than a disabled control.
 */
const READ_ONLY_SECTION_HANDLERS = Object.freeze({
  onAddSection: undefined,
  onSectionTitleSave: undefined,
  onSectionDescriptionSave: undefined,
  onSectionVisibilityChange: undefined,
  onSectionMove: undefined,
  onSectionRemove: undefined,
});

/**
 * Owns all case plan section behaviour: write access, the Active/Archived tabs, insertion,
 * field persistence, reordering, and the Delete/Archive confirmation. `useCasePlanDetailsHook`
 * only composes this hook and forwards its output.
 *
 * @param {{
 *   casePlanId: string,
 *   plan: object | null,
 *   sections: object[],
 *   setSections: (sections: object[] | ((current: object[]) => object[])) => void,
 *   reloadDetail: () => void,
 * }} params - `plan` is the detail plan summary (`isEditor`, `isWritablePlanType`, `status`).
 * @returns {object} Section state, per-section handlers (absent when read-only), and the
 *   confirmation dialog state.
 */
export const useCasePlanSection = ({
  casePlanId,
  plan,
  sections,
  setSections,
  reloadDetail,
}) => {
  const [global] = useGlobal();
  const { cbacConfigFlag, permissions, permissionIds } = global;

  const hasCasePlanEditPermission = isAnyPermitted(
    cbacConfigFlag,
    permissions,
    CASE_PLAN_SECTION_WRITE_PERMISSIONS,
    permissionIds,
  );

  // Permission, membership of this plan, writable plan type, and a plan status that still
  // allows section writes. UI gating only hides chrome — the API enforces the same rules.
  const canManageSections = useMemo(
    () => canManageCasePlanSections({ hasEditPermission: hasCasePlanEditPermission, plan }),
    [hasCasePlanEditPermission, plan],
  );

  const {
    activeSectionTab,
    onSectionTabChange,
    activeSections,
    visibleSections,
    activeSectionCount,
    isArchivedSectionTab,
  } = useCasePlanSectionTabs(sections);

  const {
    newSectionKey,
    addSection,
    saveSectionTitle,
    saveSectionDescription,
    saveSectionVisibility,
    moveSection,
    removeSection,
  } = useCasePlanSectionMutations({ casePlanId, sections, setSections, reloadDetail });

  const { requestSectionRemoval, sectionConfirmation } =
    useCasePlanSectionConfirmation(removeSection);

  // Archived sections are a read-only record even for an editor of a writable plan.
  const isSectionEditable = canManageSections && !isArchivedSectionTab;

  const destructiveAction = isSectionEditable
    ? resolveSectionDestructiveAction(plan?.status)
    : null;

  const onSectionRemove = useCallback(
    (section) => requestSectionRemoval(section, destructiveAction),
    [destructiveAction, requestSectionRemoval],
  );

  const sectionHandlers = useMemo(
    () =>
      isSectionEditable
        ? {
            onAddSection: addSection,
            onSectionTitleSave: saveSectionTitle,
            onSectionDescriptionSave: saveSectionDescription,
            onSectionVisibilityChange: saveSectionVisibility,
            onSectionMove: moveSection,
            onSectionRemove: destructiveAction === null ? undefined : onSectionRemove,
          }
        : READ_ONLY_SECTION_HANDLERS,
    [
      addSection,
      destructiveAction,
      isSectionEditable,
      moveSection,
      onSectionRemove,
      saveSectionDescription,
      saveSectionTitle,
      saveSectionVisibility,
    ],
  );

  const getSectionMoveBoundaries = useCallback(
    (sectionKey) => resolveSectionMoveBoundaries(visibleSections, sectionKey),
    [visibleSections],
  );

  // A section whose create has not answered yet has no server id, so it can never be part of
  // an export selection.
  const exportableActiveSections = useMemo(
    () => excludePendingSections(activeSections),
    [activeSections],
  );

  return {
    ...sectionHandlers,
    canManageSections,
    isSectionEditable,
    destructiveAction,
    activeSectionTab,
    onSectionTabChange,
    // Export offers the live plan only, so it consumes the active sections rather than the
    // sections currently rendered on the selected tab.
    activeSections: exportableActiveSections,
    visibleSections,
    activeSectionCount,
    newSectionKey,
    getSectionMoveBoundaries,
    sectionConfirmation,
  };
};
